import React from "react";
import { Link } from "react-router-dom";
import { LifebuoyIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { useAuthStore } from "../../stores/authStore";

interface ManagedSupportPromoProps {
    variant?: "card" | "banner";
    className?: string;
}

const ManagedSupportPromo: React.FC<ManagedSupportPromoProps> = ({ variant = "card", className = "" }) => {
    const { user, currentOrganization, orgRole } = useAuthStore();

    const org = user?.organizations?.find((o) => o.id === currentOrganization) || user?.organizations?.[0];
    const isOwnerOrAdmin = orgRole === "owner" || orgRole === "admin";

    if (!user || !org || !isOwnerOrAdmin) return null;

    // Shopify-installed orgs never pay by card here — seats go on the Shopify
    // invoice (App Store rule 1.2.1), so the billing sentence must match.
    const billingSentence = org.isShopifyManaged
        ? "Billed monthly through Shopify Billing on your Shopify invoice."
        : "Billed in your organization's currency (USD via Stripe or INR via Razorpay).";

    if (variant === "banner") {
        return (
            <Link
                to="/managed-support"
                className={`flex items-center justify-between gap-4 rounded-panel border border-brand-orange/30 bg-brand-orange/5 px-5 py-4 transition-colors hover:bg-brand-orange/10 ${className}`}
            >
                <div className="flex items-center gap-3">
                    <LifebuoyIcon className="h-6 w-6 text-brand-orange shrink-0" />
                    <div>
                        <p className="font-semibold text-brand-ink">Need human agents too?</p>
                        <p className="text-sm text-brand-ink/60">
                            Add outsourced support agents or supervisors — staffed and managed by us. {billingSentence}
                        </p>
                    </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-brand-orange shrink-0" />
            </Link>
        );
    }

    return (
        <div className={`rounded-panel border border-brand-line bg-white p-5 ${className}`}>
            <div className="flex items-center gap-2">
                <div className="rounded-2xl bg-brand-orange/10 p-2">
                    <LifebuoyIcon className="h-5 w-5 text-brand-orange" />
                </div>
                <h3 className="font-semibold text-brand-ink">Managed Customer Support</h3>
            </div>
            <p className="mt-2 text-sm text-brand-ink/60">
                Don't want to hire and train a support team? We'll provide trained human agents and supervisors who work
                directly in your support inbox. {billingSentence}
            </p>
            <Link
                to="/managed-support"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange hover:underline"
            >
                Explore managed support
                <ArrowRightIcon className="h-4 w-4" />
            </Link>
        </div>
    );
};

export default ManagedSupportPromo;
